import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GameControllerNavContainer,
  GameHubSection,
  GameHubButton,
  GameHubIcon,
  GameHubLabel,
  GameTypePillsSection,
  GameTypePill,
  PillIcon,
  PillLabel,
  PillBadge,
  PillWrapper,
  UserProgressSection,
  ProgressWidget,
  LevelBadge,
  XPProgressBar,
  XPProgressFill,
  XPText,
  StatsContainer,
  StatBadge,
  StatIcon,
  StatValue,
  NotificationDot,
  PremiumBadge,
  PillPreview,
  PreviewTitle,
  PreviewDescription,
  PreviewGames,
  PreviewGameItem
} from '../../styles/HeartGames/GameControllerNav.styles';

interface GameCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  games: { id: string; name: string; }[];
  badge?: number;
}

interface GameControllerNavProps {
  activeCategory?: string;
  onCategoryChange?: (categoryId: string) => void;
  userLevel?: number;
  userXP?: number;
  maxXP?: number;
  achievementCount?: number;
  streakDays?: number;
  isPremium?: boolean;
  hasNotifications?: boolean;
}

const gameCategories: GameCategory[] = [
  {
    id: 'quick-sparks',
    name: 'Quick Sparks',
    icon: '⚡',
    color: '#FF6B7A',
    description: 'Fast-paced games (2-5 minutes)',
    games: [
      { id: 'binary-choice', name: 'This or That' },
      { id: 'word-association', name: 'Word Spark' },
      { id: 'rapid-fire', name: 'Quick Fire' }
    ]
  },
  {
    id: 'visual-games',
    name: 'Visual Games',
    icon: '🎨',
    color: '#4ECDC4',
    description: 'Image and color-based games',
    games: [
      { id: 'heart-visions', name: 'Heart Visions' },
      { id: 'color-match', name: 'Color Psychology' },
      { id: 'image-pairs', name: 'Picture Perfect' }
    ]
  },
  {
    id: 'story-mode',
    name: 'Story Mode',
    icon: '📖',
    color: '#9B59B6',
    description: 'Interactive narrative adventures',
    games: [
      { id: 'heart-adventures', name: 'Heart Adventures' },
      { id: 'choice-tales', name: 'Choice Tales' },
      { id: 'story-paths', name: 'Story Paths' }
    ]
  },
  {
    id: 'challenge-games',
    name: 'Challenge',
    icon: '🎲',
    color: '#F39C12',
    description: 'Competitive and gamified games',
    games: [
      { id: 'heart-bingo', name: 'Heart Bingo' },
      { id: 'heart-sync', name: 'Heart Sync' },
      { id: 'personality-quest', name: 'Personality Quest' }
    ],
    badge: 3
  }
];

export const GameControllerNav: React.FC<GameControllerNavProps> = ({
  activeCategory = 'all',
  onCategoryChange,
  userLevel = 1,
  userXP = 0,
  maxXP = 100,
  achievementCount = 0,
  streakDays = 0,
  isPremium = false,
  hasNotifications = false
}) => {
  const navigate = useNavigate();
  const [hoveredPill, setHoveredPill] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const xpProgress = Math.min((userXP / maxXP) * 100, 100);

  const handleCategoryClick = (categoryId: string) => {
    if (onCategoryChange) {
      onCategoryChange(categoryId);
    }
  };

  const handleGameHubClick = () => {
    if (onCategoryChange) {
      onCategoryChange('all');
    }
    navigate('/heart-games');
  };

  const handleAchievementsClick = () => {
    navigate('/heart-games/achievements');
  };

  const handleStreakClick = () => {
    navigate('/heart-games/streaks');
  };

  const handleGameClick = (gameId: string) => {
    navigate(`/heart-games/${gameId}`);
  };

  return (
    <GameControllerNavContainer>
      {/* Left Section - Game Hub */}
      <GameHubSection>
        <GameHubButton onClick={handleGameHubClick} title="Heart Games Hub">
          <GameHubIcon>🎮❤️</GameHubIcon>
        </GameHubButton>
        <GameHubLabel>Hub</GameHubLabel>
      </GameHubSection>

      {/* Center Section - Game Type Pills */}
      <GameTypePillsSection>
        {gameCategories.map(category => (
          <PillWrapper
            key={category.id}
            onMouseEnter={() => !isMobile && setHoveredPill(category.id)}
            onMouseLeave={() => !isMobile && setHoveredPill(null)}
          >
            <GameTypePill
              $color={category.color}
              $isActive={activeCategory === category.id}
              onClick={() => handleCategoryClick(category.id)}
              title={category.description}
            >
              <PillIcon>{category.icon}</PillIcon>
              <PillLabel>{category.name}</PillLabel>
              {category.badge && <PillBadge>{category.badge}</PillBadge>}
            </GameTypePill>

            {/* Hover Preview (Desktop only) */}
            {!isMobile && (
              <PillPreview $isVisible={hoveredPill === category.id}>
                <PreviewTitle>{category.name}</PreviewTitle>
                <PreviewDescription>{category.description}</PreviewDescription>
                <PreviewGames>
                  {category.games.map(game => (
                    <PreviewGameItem
                      key={game.id}
                      onClick={() => handleGameClick(game.id)}
                    >
                      {game.name}
                    </PreviewGameItem>
                  ))}
                </PreviewGames>
              </PillPreview>
            )}
          </PillWrapper>
        ))}
      </GameTypePillsSection>

      {/* Right Section - User Progress Widget */}
      <UserProgressSection>
        {/* XP Progress (Desktop only) */}
        <ProgressWidget>
          <LevelBadge>Level {userLevel}</LevelBadge>
          <XPProgressBar>
            <XPProgressFill $progress={xpProgress} />
          </XPProgressBar>
          <XPText>{userXP} / {maxXP} XP</XPText>
        </ProgressWidget>

        {/* Stats Container */}
        <StatsContainer>
          {/* Achievement Badge */}
          <StatBadge
            onClick={handleAchievementsClick}
            title="Achievements"
            $color="#FFD700"
          >
            <StatIcon>🏆</StatIcon>
            <StatValue>{achievementCount}</StatValue>
            {hasNotifications && <NotificationDot />}
          </StatBadge>

          {/* Streak Counter */}
          <StatBadge
            onClick={handleStreakClick}
            title={`${streakDays} day streak`}
            $color="#FF6B35"
          >
            <StatIcon $animate={streakDays > 0}>🔥</StatIcon>
            <StatValue>{streakDays}</StatValue>
          </StatBadge>

          {/* Premium Badge */}
          {isPremium && (
            <PremiumBadge title="Premium Member">
              👑
            </PremiumBadge>
          )}
        </StatsContainer>
      </UserProgressSection>
    </GameControllerNavContainer>
  );
};

// Set display name for DevTools
GameControllerNav.displayName = 'GameControllerNav';

